/**
 * Time Formatting - Display and API format functions
 *
 * SYNC: This module is shared between frontend and backend.
 */

import { DEFAULT_TIMEZONE } from './constants.mjs';
import { parseAny } from './parse.mjs';

/**
 * Format date for NOAA API calls (YYYYMMDD) in UTC
 *
 * @param {Date} date - Date object
 * @returns {string} YYYYMMDD format
 */
export function formatForNOAA(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}${month}${day}`;
}

/**
 * Format date for NOAA API with time (YYYYMMDD HH:mm) in UTC
 *
 * @param {Date} date - Date object
 * @returns {string} YYYYMMDD HH:mm format
 */
export function formatForNOAAWithTime(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${year}${month}${day} ${hours}:${minutes}`;
}

/**
 * Format date for NOAA API using local time (for frontend direct calls)
 *
 * @param {Date} date - Date object
 * @returns {string} YYYYMMDD format in local time
 */
export function formatForNOAALocal(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
}

/**
 * Format timestamp to ISO 8601 UTC string
 * This is the standard internal representation
 *
 * @param {Date|string|number} input - Any timestamp format
 * @returns {string|null} ISO string or null
 */
export function toISO(input) {
    const date = parseAny(input);
    return date ? date.toISOString() : null;
}

/**
 * Format time for display (e.g., "3:45 PM")
 *
 * @param {Date|string|number} input - Any timestamp format
 * @param {string} timezone - IANA timezone (default: America/Los_Angeles)
 * @returns {string}
 */
export function formatTime(input, timezone = DEFAULT_TIMEZONE) {
    const date = parseAny(input);
    if (!date) return '--';

    return date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone,
    });
}

/**
 * Format date and time for display (e.g., "Jan 24, 3:45 PM")
 *
 * @param {Date|string|number} input - Any timestamp format
 * @param {string} timezone - IANA timezone
 * @returns {string}
 */
export function formatDateTime(input, timezone = DEFAULT_TIMEZONE) {
    const date = parseAny(input);
    if (!date) return '--';

    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone,
    });
}

/**
 * Format with custom options
 *
 * @param {Date|string|number} input - Any timestamp format
 * @param {Intl.DateTimeFormatOptions} options - Format options
 * @param {string} timezone - IANA timezone
 * @returns {string}
 */
export function formatCustom(input, options, timezone = DEFAULT_TIMEZONE) {
    const date = parseAny(input);
    if (!date) return '--';

    return date.toLocaleString('en-US', { ...options, timeZone: timezone });
}

/**
 * Format relative time (e.g., "5m ago", "2h ago")
 *
 * @param {Date|string|number} input - Timestamp
 * @param {Date} relativeTo - Reference time (default: now)
 * @returns {string}
 */
export function formatRelative(input, relativeTo = new Date()) {
    const date = parseAny(input);
    if (!date) return 'Unknown';

    const diff = relativeTo - date;
    const mins = Math.floor(diff / 60000);

    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
    return `${Math.floor(mins / 1440)}d ago`;
}

/**
 * Format for email templates
 *
 * @param {Date|string|number} input - Timestamp
 * @param {string} timezone - IANA timezone
 * @returns {string} e.g., "January 24, 2024 at 3:45 PM PST"
 */
export function formatForEmail(input, timezone = DEFAULT_TIMEZONE) {
    const date = parseAny(input);
    if (!date) return '--';

    return date.toLocaleString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short',
        timeZone: timezone,
    });
}

/**
 * Format timestamp for footer display with timezone abbreviation
 *
 * @param {Date|string|number} input - Timestamp
 * @returns {string} e.g., "Jan 24, 3:45:30 PM PST"
 */
export function formatTimestamp(input = new Date()) {
    const date = parseAny(input);
    if (!date) return '--';

    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZoneName: 'short',
    });
}

/**
 * Format date for display (e.g., "Jan 24")
 * Used in email subjects and short displays
 *
 * @param {Date|string|number} input - Any timestamp format
 * @returns {string}
 */
export function formatDateShort(input = new Date()) {
    const date = parseAny(input);
    if (!date) return '--';

    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    });
}

/**
 * Format full date for display (e.g., "Monday, January 24")
 * Used in daily digest emails
 *
 * @param {Date|string|number} input - Any timestamp format
 * @returns {string}
 */
export function formatDateFull(input = new Date()) {
    const date = parseAny(input);
    if (!date) return '--';

    return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    });
}

// ===========================================
// Core Time Getters
// ===========================================

/**
 * Get current timestamp in milliseconds
 * Single source of truth for "now" as a timestamp
 *
 * @returns {number} Current time in milliseconds
 */
export function timestamp() {
    return Date.now();
}

/**
 * Get current Date object
 * Single source of truth for "now" as a Date
 *
 * @returns {Date} Current Date object
 */
export function now() {
    return new Date();
}

// ===========================================
// Duration Formatting
// ===========================================

/**
 * Format minutes as a countdown string (e.g., "2h 30m" or "45m")
 * Replaces patterns like: hours > 0 ? `${hours}h ${mins}m` : `${mins}m`
 *
 * @param {number} totalMinutes - Total minutes (can be negative for past)
 * @param {Object} options - Formatting options
 * @param {boolean} options.alwaysShowHours - Always show hours even if 0
 * @param {boolean} options.verbose - Use "min" instead of "m"
 * @returns {string} Formatted countdown string
 */
export function formatCountdown(totalMinutes, options = {}) {
    if (totalMinutes === null || totalMinutes === undefined || isNaN(totalMinutes)) {
        return '--';
    }

    const { alwaysShowHours = false, verbose = false } = options;
    const total = Math.abs(Math.round(totalMinutes));
    const hours = Math.floor(total / 60);
    const minutes = total % 60;

    const minLabel = verbose ? ' min' : 'm';
    const hourLabel = 'h';

    if (hours > 0 || alwaysShowHours) {
        return `${hours}${hourLabel} ${minutes}${minLabel}`;
    }
    return `${minutes}${minLabel}`;
}

/**
 * Format seconds as duration string (e.g., "2h 30m" or "10:45:30")
 *
 * @param {number} totalSeconds - Total seconds
 * @param {Object} options - Formatting options
 * @param {boolean} options.colonFormat - Use "HH:MM" format instead of "Xh Ym"
 * @param {boolean} options.showSeconds - Include seconds in output
 * @returns {string} Formatted duration string
 */
export function formatDuration(totalSeconds, options = {}) {
    if (totalSeconds === null || totalSeconds === undefined || isNaN(totalSeconds)) {
        return '--';
    }

    const { colonFormat = false, showSeconds = false } = options;
    const total = Math.abs(Math.round(totalSeconds));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    if (colonFormat) {
        const h = String(hours).padStart(2, '0');
        const m = String(minutes).padStart(2, '0');
        if (showSeconds) {
            const s = String(seconds).padStart(2, '0');
            return `${h}:${m}:${s}`;
        }
        return `${h}:${m}`;
    }

    // Human-readable format
    if (hours > 0) {
        if (showSeconds) {
            return `${hours}h ${minutes}m ${seconds}s`;
        }
        return `${hours}h ${minutes}m`;
    }
    if (showSeconds) {
        return `${minutes}m ${seconds}s`;
    }
    return `${minutes}m`;
}

/**
 * Format a countdown with "In X" prefix for future times
 * Used for beach access and similar UI elements
 *
 * @param {number} totalMinutes - Minutes until event
 * @param {Object} options - Formatting options
 * @param {boolean} options.showOnlyHours - Only show hours (e.g., "In 2h")
 * @returns {string} Formatted string like "In 2h 30m"
 */
export function formatTimeUntil(totalMinutes, options = {}) {
    if (totalMinutes === null || totalMinutes === undefined || isNaN(totalMinutes)) {
        return '--';
    }

    const { showOnlyHours = false } = options;
    const total = Math.abs(Math.round(totalMinutes));
    const hours = Math.floor(total / 60);
    const minutes = total % 60;

    if (showOnlyHours && hours > 0) {
        return `In ${hours}h`;
    }
    if (hours > 0) {
        return `In ${hours}h ${minutes}m`;
    }
    return `In ${minutes}m`;
}
